import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
// SPDX-License-Identifier: AGPL-3.0-only
import { useId, useMemo } from "react";
import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark, faClock, faDownload, faLock, } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { verifyBallotLogProof } from "@sequentech/ui-core";
import { useZonedFormat } from "@/hooks/useZonedFormat";
import { shortened } from "./logEntryDetails";
var GROUPS = [
    { kind: "BallotReceived", checks: ["binding", "received", "ballotId"], label: "received" },
    { kind: "CastVote", checks: ["cast"], label: "cast" },
];
var STATUS_ICONS = { passed: faCircleCheck, failed: faCircleXmark, pending: faClock };
var STATUS_COLORS = { passed: "success.main", failed: "error.main", pending: "text.secondary" };
var combinedStatus = function (statuses) {
    return statuses.includes("failed") ? "failed" : statuses.includes("pending") ? "pending" : "passed";
};
export var BallotEntryEvidence = function (_a) {
    var _b, _c;
    var proof = _a.proof, trust = _a.trust, selectedEntry = _a.selectedEntry, zone = _a.zone, onRetry = _a.onRetry;
    var _d = useTranslation(), t = _d.t, i18n = _d.i18n;
    var format = useZonedFormat(zone, { seconds: true });
    var headingId = useId();
    var checked = useMemo(function () {
        try {
            return verifyBallotLogProof(trust, proof);
        }
        catch (_a) {
            return null;
        }
    }, [trust, proof]);
    var state = combinedStatus(GROUPS.flatMap(function (_a) {
        var checks = _a.checks;
        return checks.map(function (key) { var _a; return (_a = checked === null || checked === void 0 ? void 0 : checked.checks[key]) !== null && _a !== void 0 ? _a : "failed"; });
    }));
    var voterKey = (_c = (_b = proof.log_entries) === null || _b === void 0 ? void 0 : _b.find(function (_a) {
        var kind = _a.kind;
        return kind === "BallotReceived";
    })) === null || _c === void 0 ? void 0 : _c.voter_public_key;
    var download = function () {
        var blob = new Blob([JSON.stringify({ trust: trust, proof: proof }, null, 2)], { type: "application/json" });
        var url = URL.createObjectURL(blob);
        var link = document.createElement("a");
        link.href = url;
        link.download = "ballot-".concat(proof.ballot_id.replace(/[^A-Z0-9-]/gi, ""), "-signed-entries.json");
        link.click();
        setTimeout(function () { return URL.revokeObjectURL(url); }, 0);
    };
    return (_jsxs(Stack, { spacing: 1.5, sx: { minWidth: 0 }, children: [_jsx(Box, { component: "dl", sx: {
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    gap: 1.5,
                    m: 0,
                }, children: [
                    { label: t("ballotPrivacy.admin.voterKey"), value: voterKey },
                    { label: t("ballotPrivacy.boxKey"), value: trust.ballot_box_key.public_key },
                ].map(function (_a) {
                    var label = _a.label, value = _a.value;
                    return (_jsxs(Box, { sx: {
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "8rem minmax(0, 1fr)" },
                            columnGap: 2,
                            minWidth: 0,
                        }, children: [_jsx(Typography, { component: "dt", variant: "body2", color: "text.secondary", children: label }), _jsx(Typography, { component: "dd", variant: "body2", title: value !== null && value !== void 0 ? value : undefined, sx: {
                                    m: 0,
                                    fontFamily: '"DejaVu Sans Mono", ui-monospace, monospace',
                                    fontSize: 12.5,
                                    overflowWrap: "anywhere",
                                }, children: value ? shortened(value, 16, 4) : "—" })] }, label));
                }) }), state !== "passed" ? (_jsxs(Alert, { severity: state === "failed" ? "error" : "info", children: [_jsx(Typography, { variant: "body2", fontWeight: 600, sx: { m: 0 }, children: t("ballotPrivacy.".concat(state)) }), t("ballotPrivacy.".concat(state === "failed" ? "failedHelp" : "pendingHelp"))] })) : null, _jsxs(Box, { sx: {
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    gap: 1,
                    pt: 0.5,
                }, children: [_jsx(Typography, { component: "h4", id: headingId, variant: "body2", color: "text.secondary", sx: { m: 0 }, children: t("ballotPrivacy.admin.entries") }), state === "passed" ? (_jsx(Typography, { variant: "caption", color: "success.main", children: t("ballotPrivacy.passed") })) : null] }), _jsx(Box, { component: "ol", "aria-labelledby": headingId, sx: {
                    listStyle: "none",
                    m: 0,
                    p: 0,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                }, children: GROUPS.map(function (_a, index) {
                    var _b, _c, _d;
                    var kind = _a.kind, checks = _a.checks, label = _a.label;
                    var status = combinedStatus(checks.map(function (key) { var _a; return (_a = checked === null || checked === void 0 ? void 0 : checked.checks[key]) !== null && _a !== void 0 ? _a : "failed"; }));
                    var metadata = (_b = proof.log_entries) === null || _b === void 0 ? void 0 : _b.find(function (entry) { return entry.kind === kind; });
                    var current = metadata
                        ? String(selectedEntry.id) === metadata.id
                        : selectedEntry.statement_kind === kind;
                    var timestamp = (_c = metadata === null || metadata === void 0 ? void 0 : metadata.timestamp) !== null && _c !== void 0 ? _c : (current ? selectedEntry.statement_timestamp : null);
                    var id = (_d = metadata === null || metadata === void 0 ? void 0 : metadata.id) !== null && _d !== void 0 ? _d : (current ? String(selectedEntry.id) : null);
                    var number = id && Number.isSafeInteger(Number(id))
                        ? Number(id).toLocaleString(i18n.language)
                        : id;
                    return (_jsxs(Box, { component: "li", "aria-current": current ? "true" : undefined, "data-entry-kind": kind, "data-check-state": status, "data-check": checks.join(" "), sx: {
                            display: "grid",
                            gridTemplateColumns: "22px minmax(0, 1fr)",
                            columnGap: 1.5,
                            px: 1.5,
                            py: 1.25,
                            borderTop: index ? 1 : 0,
                            borderColor: "divider",
                            bgcolor: current ? "#F8FAFC" : "transparent",
                            boxShadow: current ? "inset 3px 0 0 #0F054C" : "none",
                        }, children: [_jsx(Box, { sx: { color: STATUS_COLORS[status], pt: 0.25 }, children: _jsx(FontAwesomeIcon, { icon: STATUS_ICONS[status], "aria-label": t("ballotPrivacy.states.".concat(status)) }) }), _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsxs(Box, { sx: {
                                            display: "flex",
                                            flexWrap: "wrap",
                                            alignItems: "baseline",
                                            columnGap: 1.5,
                                            rowGap: 0.5,
                                        }, children: [_jsxs(Typography, { component: "span", variant: "body2", fontWeight: 600, sx: { m: 0, lineHeight: 1.5, overflowWrap: "anywhere" }, children: [t("ballotPrivacy.admin.".concat(label, "Title"), {
                                                        ballotId: proof.ballot_id,
                                                    }), kind === "CastVote" && proof.voting_channel
                                                        ? ", ".concat(t("ballotPrivacy.channels.".concat(proof.voting_channel.toLowerCase()), { defaultValue: proof.voting_channel }))
                                                        : null] }), id || timestamp ? (_jsxs(Typography, { variant: "caption", color: "text.secondary", children: [id
                                                        ? t("ballotPrivacy.admin.entryNumber", { id: number })
                                                        : null, id && timestamp ? " · " : null, timestamp
                                                        ? format.format(timestamp * 1000, zone)
                                                        : null] })) : null, current ? (_jsx(Chip, { label: t("ballotPrivacy.admin.thisEntry"), size: "small", sx: {
                                                    height: 20,
                                                    borderRadius: "100px",
                                                    bgcolor: "#0F054C",
                                                    color: "#FFFFFF",
                                                    fontSize: 11.5,
                                                } })) : null] }), _jsx(Typography, { component: "p", variant: "body2", sx: { m: 0, mt: 0.25, fontSize: 13.5, lineHeight: 1.5 }, children: t("ballotPrivacy.admin.".concat(label, "Help")) }), status !== "passed" ? (_jsx(Typography, { variant: "body2", sx: { color: STATUS_COLORS[status] }, children: t("ballotPrivacy.states.".concat(status)) })) : null] })] }, kind));
                }) }), _jsx(Alert, { severity: "info", icon: _jsx(FontAwesomeIcon, { icon: faLock }), variant: "outlined", sx: {
                    "bgcolor": "#F7F9FC",
                    "borderColor": "#DFE6EF",
                    "color": "text.primary",
                    "& .MuiAlert-icon": { color: "#65758B", fontSize: 13 },
                }, children: t("ballotPrivacy.admin.neverPublished") }), _jsxs(Stack, { direction: "row", spacing: 1, useFlexGap: true, sx: { flexWrap: "wrap" }, children: [_jsx(Button, { variant: "text", startIcon: _jsx(FontAwesomeIcon, { icon: faDownload }), size: "small", onClick: download, sx: { p: 0, minHeight: 28 }, children: t("ballotPrivacy.download") }), state === "pending" ? (_jsx(Button, { size: "small", onClick: onRetry, children: t("ballotPrivacy.retry") })) : null] })] }));
};
